import React from 'react';
import { useTranslation } from 'react-i18next';

import { IconClose } from '../Icon';
import { Portal, PortalNamespace } from '../Portal';
import { SrOnly } from '../SrOnly';
import {
  Backdrop,
  Center,
  CloseBtn,
  Dialog,
  Footer,
  FooterItem,
  Heading,
  Illustration,
  Wrapper,
} from './wrapper';

interface ModalIllustrationProps {
  src: string;
  srcSet?: string;
  alt: string;
}

interface ModalProps {
  heading?: string;
  illustration?: ModalIllustrationProps;
  disableCloseBtnShapeOutside?: boolean; // Disabled css property shape-outside on CloseBtn
  children: React.ReactNode;
  footerActions?: React.ReactNode[];
  disableBackdropClose?: boolean;
  onClose: (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>) => void;
}

const Modal = ({
  heading,
  illustration,
  disableCloseBtnShapeOutside,
  children,
  footerActions,
  disableBackdropClose,
  onClose,
}: ModalProps) => {
  const { t } = useTranslation(['common']);

  return (
    <Portal id={PortalNamespace.DefaultPortal}>
      <Wrapper>
        <Backdrop
          onClick={disableBackdropClose ? undefined : onClose}
          aria-label={t('closeModal', { ns: 'common' })}
        >
          <Center>
            <Dialog onClick={(e) => e.stopPropagation()}>
              <CloseBtn
                onClick={onClose}
                title={t('closeModal', { ns: 'common' })}
                $absolutePosition={!!illustration}
                $disabledShapeOutside={disableCloseBtnShapeOutside}
                data-testid="modal-close-button"
              >
                <SrOnly>{t('closeModal', { ns: 'common' })}</SrOnly>
                <IconClose aria-hidden="true" />
              </CloseBtn>
              {illustration && (
                <Illustration
                  src={illustration.src}
                  srcSet={illustration.srcSet}
                  alt={illustration.alt}
                  aria-hidden="true"
                />
              )}
              {heading && <Heading>{heading}</Heading>}
              {children}
              {footerActions && (
                <Footer>
                  {footerActions.map((action, i) => (
                    <FooterItem key={`modal-action-item-${i}`}>{action}</FooterItem>
                  ))}
                </Footer>
              )}
            </Dialog>
          </Center>
        </Backdrop>
      </Wrapper>
    </Portal>
  );
};

export default Modal;
